import { AxiosResponse } from 'axios';
import { API_MENU_REPORT_ROUTE } from 'config/api-consts';
import { httpConfig } from 'config/http';
import { API_BASE_URL } from 'core/config';
import { url } from 'core/helpers/string';
import { Repository } from 'core/repositories/Repository';
import { buildTree } from 'helpers/tree';
import kebabCase from 'lodash/kebabCase';
import { FileTemplate } from 'models/FileTemplate';
import { FileTemplateInput } from 'models/FileTemplateInput';
import { Menu } from 'models/Menu';
import { MenuFilter } from 'models/MenuFilter';
import { MenuReport } from 'models/MenuReport';
import { MenuReportFilter } from 'models/MenuReportFilter';
import { Organization } from 'models/Organization';
import { OrganizationFilter } from 'models/OrganizationFilter';
import { Site } from 'models/Site';
import { SiteFilter } from 'models/SiteFilter';
import { Status } from 'models/Status';
import { StatusFilter } from 'models/StatusFilter';
import { BatchId, PureModelData } from 'react3l';
import nameof from 'ts-nameof.macro';

export class MenuReportRepository extends Repository {
  constructor() {
    super(httpConfig);
    this.setBaseURL(url(API_BASE_URL, API_MENU_REPORT_ROUTE));
  }

  public count = (menuReportFilter?: MenuReportFilter): Promise<number> => {
    return this.http
      .post<number>(kebabCase(nameof(this.count)), menuReportFilter)
      .then((response: AxiosResponse<number>) => response.data);
  };

  public list = (
    menuReportFilter?: MenuReportFilter,
  ): Promise<MenuReport[]> => {
    return this.http
      .post<MenuReport[]>(kebabCase(nameof(this.list)), menuReportFilter)
      .then((response: AxiosResponse<MenuReport[]>) => {
        return response.data?.map((menuReport: PureModelData<MenuReport>) =>
          MenuReport.clone<MenuReport>(menuReport),
        );
      });
  };

  public get = (id: number | string): Promise<MenuReport> => {
    return this.http
      .post<MenuReport>(kebabCase(nameof(this.get)), { id })
      .then((response: AxiosResponse<MenuReport>) =>
        MenuReport.clone<MenuReport>(response.data),
      );
  };

  public create = (menuReport: MenuReport): Promise<MenuReport> => {
    return this.http
      .post<MenuReport>(kebabCase(nameof(this.create)), menuReport)
      .then((response: AxiosResponse<PureModelData<MenuReport>>) =>
        MenuReport.clone<MenuReport>(response.data),
      );
  };

  public update = (menuReport: MenuReport): Promise<MenuReport> => {
    return this.http
      .post<MenuReport>(kebabCase(nameof(this.update)), menuReport)
      .then((response: AxiosResponse<MenuReport>) =>
        MenuReport.clone<MenuReport>(response.data),
      );
  };

  public delete = (menuReport: MenuReport): Promise<MenuReport> => {
    return this.http
      .post<MenuReport>(kebabCase(nameof(this.delete)), menuReport)
      .then((response: AxiosResponse<MenuReport>) =>
        MenuReport.clone<MenuReport>(response.data),
      );
  };

  public save = (menuReport: MenuReport): Promise<MenuReport> => {
    return menuReport.id ? this.update(menuReport) : this.create(menuReport);
  };

  public singleListStatus = (): Promise<Status[]> => {
    return this.http
      .post<Status[]>(
        kebabCase(nameof(this.singleListStatus)),
        new StatusFilter(),
      )
      .then((response: AxiosResponse<Status[]>) => {
        return response.data.map((status: PureModelData<Status>) =>
          Status.clone<Status>(status),
        );
      });
  };

  public filterListStatus = (): Promise<Status[]> => {
    return this.http
      .post<Status[]>(
        kebabCase(nameof(this.filterListStatus)),
        new StatusFilter(),
      )
      .then((response: AxiosResponse<Status[]>) => {
        return response.data.map((status: PureModelData<Status>) =>
          Status.clone<Status>(status),
        );
      });
  };
  public singleListSite = (): Promise<Site[]> => {
    return this.http
      .post<Site[]>(kebabCase(nameof(this.singleListSite)), new SiteFilter())
      .then((response: AxiosResponse<Site[]>) => {
        return response.data.map((site: PureModelData<Site>) =>
          Site.clone<Site>(site),
        );
      });
  };

  public filterListSite = (): Promise<Site[]> => {
    return this.http
      .post<Site[]>(kebabCase(nameof(this.filterListSite)), new SiteFilter())
      .then((response: AxiosResponse<Site[]>) => {
        return response.data.map((site: PureModelData<Site>) =>
          Site.clone<Site>(site),
        );
      });
  };

  public singleListMenu = (): Promise<Menu[]> => {
    return this.http
      .post<Menu[]>(kebabCase(nameof(this.singleListMenu)), new MenuFilter())
      .then((response: AxiosResponse<Menu[]>) => {
        return response.data.map((menu: PureModelData<Menu>) =>
          Menu.clone<Menu>(menu),
        );
      });
  };

  public filterListMenu = (): Promise<Menu[]> => {
    return this.http
      .post<Menu[]>(kebabCase(nameof(this.filterListMenu)), new MenuFilter())
      .then((response: AxiosResponse<Menu[]>) => {
        return response.data.map((menu: PureModelData<Menu>) =>
          Menu.clone<Menu>(menu),
        );
      });
  };

  public bulkDelete = (idList: BatchId): Promise<void> => {
    return this.http
      .post(kebabCase(nameof(this.bulkDelete)), idList)
      .then((response: AxiosResponse<void>) => response.data);
  };
  public singleListOrganization = (
    organizationFilter: OrganizationFilter,
  ): Promise<Organization[]> => {
    return this.http
      .post<Organization[]>(
        kebabCase(nameof(this.singleListOrganization)),
        organizationFilter,
      )
      .then((response: AxiosResponse<Organization[]>) => {
        return buildTree(
          response.data.map((organization: PureModelData<Organization>) =>
            Organization.clone<Organization>(organization),
          ),
        );
      });
  };
  public import = (file: File, name: string = nameof(file)): Promise<void> => {
    const formData: FormData = new FormData();
    formData.append(name, file);
    return this.http
      .post<void>(kebabCase(nameof(this.import)), formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((response: AxiosResponse<void>) => response.data);
  };

  public export = (
    menuReportFilter?: MenuReportFilter,
  ): Promise<AxiosResponse<any>> => {
    return this.http.post('export', menuReportFilter, {
      responseType: 'arraybuffer',
    });
  };

  public exportTemplate = (
    menuReportFilter?: MenuReportFilter,
  ): Promise<AxiosResponse<any>> => {
    return this.http.post('export-template', menuReportFilter, {
      responseType: 'arraybuffer',
    });
  };

  public upload = (file: File): Promise<boolean> => {
    const formData: FormData = new FormData();
    formData.append('file', file);
    return this.http
      .post<boolean>(kebabCase(nameof(this.upload)), formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then((response: AxiosResponse<boolean>) => response.data);
  };

  public validateInputs = (params: {
    menuReportId: number;
    fileTemplateInputs: FileTemplateInput[];
  }): Promise<FileTemplate> => {
    return this.http
      .post<FileTemplate>(kebabCase(nameof(this.validateInputs)), params)
      .then((response: AxiosResponse<FileTemplate>) =>
        FileTemplate.clone<FileTemplate>(response.data),
      );
  };
}

export const menuReportRepository: MenuReportRepository = new MenuReportRepository();
